import { RelatedApps, RELATED_BY_APP } from '@hypneuronerd/ui';
import type { Reflection } from '../data/types';
import styles from './ReflectionView.module.css';

interface ReflectionViewProps {
  reflection: Reflection;
  /** The lines the visitor actually chose, in order. Empty if the
   * playthrough was resumed mid-scenario (we don't persist in-flight
   * path history). */
  path: string[];
  onReplay: () => void;
  onList: () => void;
}

export function ReflectionView({ reflection, path, onReplay, onList }: ReflectionViewProps) {
  return (
    <section className={styles.frame}>
      <header className={styles.header}>
        <span className={styles.label}>reflection</span>
        <h1 className={styles.title}>{reflection.title}</h1>
      </header>

      <section>
        <p className={styles.skillLabel}>what this scenario practices</p>
        <p className={styles.skill}>{reflection.skillPracticed}</p>
      </section>

      {/* The visitor's actual path through the scenario as a numbered
       * trail. Sits above the "other paths" section so the comparison
       * is immediate: "this is what you said; here's what else was
       * possible." */}
      {path.length > 0 && (
        <section className={styles.yourPath}>
          <h2 className={styles.yourPathTitle}>what you chose</h2>
          <ol className={styles.pathTrail}>
            {path.map((line, i) => (
              <li key={i} className={styles.pathStep}>
                <span className={styles.pathIndex}>{i + 1}.</span>
                <span className={styles.pathLine}>{line}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      <p className={styles.body}>{reflection.body}</p>

      {reflection.otherPaths.length > 0 && (
        <section className={styles.otherPaths}>
          <h2 className={styles.otherTitle}>other paths you could have taken</h2>
          <ul className={styles.pathList}>
            {reflection.otherPaths.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </section>
      )}

      <footer className={styles.actions}>
        <button type="button" className={styles.primary} onClick={onReplay}>
          replay this scenario
        </button>
        <button type="button" className={styles.secondary} onClick={onList}>
          back to scenarios
        </button>
      </footer>

      <RelatedApps apps={RELATED_BY_APP['negotiation-sim']} surface="midnight" />
    </section>
  );
}
